/**
 * Option 2: Bidirectional Counter with Cart Attributes Persistence
 * - Random initial values (5-24)
 * - Goes up AND down randomly (realistic cart activity)
 * - Persists in cart attributes (no flickering)
 * - Survives quantity changes and page refreshes
 */

(function() {
  'use strict';
  
  console.log('🎯 custom.js (Option 2) loaded');
  
  // Configuration
  const CONFIG = {
    minCarts: 5,
    maxCarts: 24,
    firstUpdateDelay: 3000,
    updateInterval: 5000,
    animationDuration: 400,
    minStepsBeforeDirectionChange: 2,
    maxStepsBeforeDirectionChange: 4
  };
  
  // Counter state for each variant
  class CounterState {
    constructor(variantId, initialValue) {
      this.variantId = variantId;
      this.value = initialValue;
      this.direction = Math.random() > 0.5 ? 'up' : 'down';
      this.stepsInDirection = 0;
      this.stepsBeforeSwitch = this.randomSteps();
      this.element = null;
      
      // Shared base styles to prevent layout shifts
      this.baseStyles = 'color: #1D2F25; font-weight: 700; font-size: 12px; border-radius: 4px; display: inline-block !important; width: auto !important; min-width: 20px !important; text-align: center; margin: 0px; padding: 4px; line-height: 1; vertical-align: baseline;';
      
      console.log(`🎯 Counter ${variantId}: start=${initialValue}, direction=${this.direction}, switchAfter=${this.stepsBeforeSwitch}`);
    }
    
    randomSteps() {
      return Math.floor(Math.random() * (CONFIG.maxStepsBeforeDirectionChange - CONFIG.minStepsBeforeDirectionChange + 1)) + CONFIG.minStepsBeforeDirectionChange;
    }
    
    update() {
      if (this.stepsInDirection >= this.stepsBeforeSwitch) {
        this.direction = this.direction === 'up' ? 'down' : 'up';
        this.stepsInDirection = 0;
        this.stepsBeforeSwitch = this.randomSteps();
        console.log(`🎯 ${this.variantId}: Switched to ${this.direction}, will switch after ${this.stepsBeforeSwitch} steps`);
      }
      
      const oldValue = this.value;
      
      if (this.direction === 'up') {
        this.value++;
        if (this.value > CONFIG.maxCarts) {
          this.value = CONFIG.maxCarts;
          this.direction = 'down';
          this.stepsInDirection = 0;
          this.stepsBeforeSwitch = this.randomSteps();
          console.log(`🎯 ${this.variantId}: Hit ceiling, forcing down`);
        }
      } else {
        this.value--;
        if (this.value < CONFIG.minCarts) {
          this.value = CONFIG.minCarts;
          this.direction = 'up';
          this.stepsInDirection = 0;
          this.stepsBeforeSwitch = this.randomSteps();
          console.log(`🎯 ${this.variantId}: Hit floor, forcing up`);
        }
      }
      
      this.stepsInDirection++;
      
      console.log(`🎯 ${this.variantId}: ${oldValue} → ${this.value} (${this.direction}, step ${this.stepsInDirection}/${this.stepsBeforeSwitch})`);
      
      return true;
    }
    
    render() {
      if (!this.element || !document.contains(this.element)) {
        return;
      }
      this.element.textContent = this.value;
    }
    
    animate() {
      if (!this.element) {
        console.warn(`🎯 Cannot animate: no element for ${this.variantId}`);
        return;
      }
      
      console.log(`🎯 Animating ${this.variantId} with value ${this.value}`);
      
      // Apply animated state using shared base styles
      this.element.style.cssText = this.baseStyles + ' transition: background-color 0.4s ease, color 0.4s ease; background-color: #1D2F25; color: #fff;';
      
      setTimeout(() => {
        if (this.element) {
          // Return to normal state
          this.element.style.cssText = this.baseStyles + ' transition: background-color 0.4s ease, color 0.4s ease; background-color: transparent; color: #1D2F25;';
          console.log(`🎯 Animation complete for ${this.variantId}`);
        }
      }, CONFIG.animationDuration);
    }
  }
  
  // Global Counter Manager
  const CounterManager = {
    states: new Map(),
    timers: new Map(), // Individual timers for each variant
    
    init() {
      console.log('🎯 CounterManager.init()');
      this.bindAll();
    },
    
    startTimer(variantId, index = 0) {
      // Clear existing timer if any
      if (this.timers.has(variantId)) {
        const { firstTimer, intervalTimer } = this.timers.get(variantId);
        clearTimeout(firstTimer);
        if (intervalTimer) clearInterval(intervalTimer);
        console.log(`🎯 Cleared existing timer for ${variantId}`);
      }
      
      const state = this.states.get(variantId);
      if (!state) {
        console.error(`🎯 ERROR: No state found for ${variantId}`);
        return;
      }
      
      // Stagger delays based on index
      const staggerDelay = index * 2500;
      const initialDelay = CONFIG.firstUpdateDelay + staggerDelay;
      const updateInterval = CONFIG.updateInterval + staggerDelay;
      
      console.log(`🎯 Starting timer for ${variantId} (index ${index}): initialDelay=${initialDelay}ms, interval=${updateInterval}ms`);
      
      const firstTimer = setTimeout(() => {
        console.log(`🎯 First update firing for ${variantId}`);
        this.updateCounter(variantId);
        
        const intervalTimer = setInterval(() => {
          console.log(`🎯 Interval update firing for ${variantId}`);
          this.updateCounter(variantId);
        }, updateInterval);
        
        this.timers.set(variantId, { firstTimer, intervalTimer });
      }, initialDelay);
      
      this.timers.set(variantId, { firstTimer, intervalTimer: null });
      console.log(`🎯 Timer scheduled for ${variantId} - will fire in ${initialDelay}ms`);
    },
    
    updateCounter(variantId) {
      const state = this.states.get(variantId);
      if (!state) {
        console.error(`🎯 ERROR: Cannot update - no state for ${variantId}`);
        return;
      }
      
      if (!state.element) {
        console.error(`🎯 ERROR: Cannot update ${variantId} - element is null`);
        return;
      }
      
      if (!document.contains(state.element)) {
        console.warn(`🎯 WARNING: Element not in DOM for ${variantId}, searching for new element`);
        
        // Try to find the element again
        const container = document.querySelector(`.e-in-x-cart[data-variant-id="${variantId}"]`);
        if (container) {
          const newElement = container.querySelector('.e-in-x-cart__counter');
          if (newElement) {
            console.log(`🎯 Found new element for ${variantId}, updating reference`);
            state.element = newElement;
          } else {
            console.error(`🎯 ERROR: Could not find counter element for ${variantId}`);
            return;
          }
        } else {
          console.error(`🎯 ERROR: Could not find container for ${variantId}`);
          return;
        }
      }
      
      console.log(`🎯 Updating counter ${variantId}`);
      state.update();
      state.render();
      state.animate();
      
      this.saveToCart(variantId, state.value, state.direction, state.stepsInDirection, state.stepsBeforeSwitch);
    },
    
    bindAll() {
      console.log('🎯 CounterManager.bindAll() called');
      const containers = document.querySelectorAll('.e-in-x-cart');
      console.log(`🎯 Found ${containers.length} counter containers`);
      
      containers.forEach((container, index) => {
        const variantId = container.dataset.variantId;
        const counterEl = container.querySelector('.e-in-x-cart__counter');
        const isInitiallyGenerated = container.dataset.initiallyGenerated === 'true';
        
        console.log(`🎯 Processing counter ${index}: variantId=${variantId}, hasElement=${!!counterEl}`);
        
        if (!variantId || !counterEl) {
          console.warn(`🎯 Skipping counter ${index}: missing variantId or element`);
          return;
        }
        
        const initialValue = parseInt(counterEl.textContent) || this.randomInt(CONFIG.minCarts, CONFIG.maxCarts);
        
        // Check if this is a NEW counter (first time)
        const isNewCounter = !this.states.has(variantId);
        
        if (isNewCounter) {
          // New counter - create state and start timer
          console.log(`🎯 NEW counter ${variantId} with value ${initialValue} at index ${index}`);
          const state = new CounterState(variantId, initialValue);
          this.states.set(variantId, state);
          state.element = counterEl;
          
          if (isInitiallyGenerated) {
            console.log(`🎯 Saving initial value ${initialValue} for ${variantId}`);
            this.saveToCart(variantId, initialValue, state.direction, state.stepsInDirection, state.stepsBeforeSwitch);
          }
          
          // Apply styling with fixed width to prevent layout shift (override theme CSS)
          counterEl.style.cssText = state.baseStyles + ' transition: background-color 0.4s ease, color 0.4s ease; background-color: transparent;';
          
          state.render();
          
          // Start timer for this counter
          this.startTimer(variantId, index);
          
        } else {
          // Existing counter - just update element reference, DON'T restart timer
          console.log(`🎯 REBINDING existing counter ${variantId} - keeping timer`);
          const state = this.states.get(variantId);
          state.element = counterEl;
          
          // Apply styling with fixed width to prevent layout shift (override theme CSS)
          counterEl.style.cssText = 'color: #1D2F25 !important; font-weight: 700 !important; font-size: 12px !important; border-radius: 4px !important; display: inline-block !important; width: auto !important; min-width: 20px !important; text-align: center !important; margin: 0px !important; padding: 2px 4px !important; line-height: 1 !important; vertical-align: baseline !important; transition: background-color 0.4s ease, color 0.4s ease !important;';
          
          // Render current state value (important after DOM recreate)
          state.render();
        }
        
        console.log(`🎯 Finished binding ${variantId} with value ${this.states.get(variantId).value}`);
      });
      
      console.log(`🎯 bindAll() complete: ${this.states.size} total states, ${this.timers.size} total timers`);
    },
    
    tick() {
      // This method is no longer needed - using individual timers
    },
    
    saveToCart(variantId, value, direction, stepsInDirection, stepsBeforeSwitch) {
      const attributes = {
        [`counter_${variantId}`]: value,
        [`counter_${variantId}_dir`]: direction,
        [`counter_${variantId}_steps`]: stepsInDirection,
        [`counter_${variantId}_switch`]: stepsBeforeSwitch
      };
      
      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attributes })
      })
      .then(response => response.json())
      .then(cart => {
        console.log(`🎯 Saved ${variantId}: ${value} (${direction})`);
      })
      .catch(error => {
        console.error(`🎯 Error saving ${variantId}:`, error);
      });
    },
    
    randomInt(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    }
  };
  
  // Bundle & Discount Helper Functions
  function hideBundleDiscountCodes() {
    const discountElements = document.querySelectorAll('.discounts__discount');
    discountElements.forEach(element => {
      if (element.textContent && element.textContent.includes('XRTM8JMCFWN3')) {
        element.style.display = 'none';
        element.style.height = '0';
        element.style.margin = '0';
        element.style.padding = '0';
        element.style.overflow = 'hidden';
      }
    });
    
    const discountSections = document.querySelectorAll('.discounts');
    discountSections.forEach(section => {
      if (section.textContent && section.textContent.includes('XRTM8JMCFWN3')) {
        section.style.display = 'none';
        section.style.height = '0';
        section.style.margin = '0';
        section.style.padding = '0';
        section.style.overflow = 'hidden';
      }
    });
    
    const cartItems = document.querySelectorAll('.product-cart-item-info .discounts');
    cartItems.forEach(section => {
      section.style.display = 'none';
      section.style.height = '0';
      section.style.margin = '0';
      section.style.padding = '0';
    });
  }
  
  function initDiscountWatcher() {
    const cartDrawer = document.getElementById('Cart-Drawer');
    if (!cartDrawer) return;
    
    const discountObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1) {
              if (node.classList && node.classList.contains('discounts__discount')) {
                if (node.textContent.includes('XRTM8JMCFWN3')) {
                  node.style.display = 'none';
                  node.style.height = '0';
                }
              }
              
              const discountChildren = node.querySelectorAll && node.querySelectorAll('.discounts__discount');
              if (discountChildren) {
                discountChildren.forEach(function(discount) {
                  if (discount.textContent.includes('XRTM8JMCFWN3')) {
                    discount.style.display = 'none';
                    discount.style.height = '0';
                  }
                });
              }
            }
          });
        }
      });
    });
    
    discountObserver.observe(cartDrawer, {
      childList: true,
      subtree: true
    });
  }
  
  function enforceGiftRatio() {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const nordicGlowItems = cart.items.filter(item => 
          item.product_title.includes('NordicGlow') || item.handle === 'nordicglow'
        );
        
        const purifyItems = cart.items.filter(item => {
          const isFreeGift = item.properties && item.properties._isFreeGift === 'true';
          const isPurify = item.product_title.includes('Purify') || item.handle === 'purify';
          return isPurify && isFreeGift;
        });
        
        if (nordicGlowItems.length === 0 && purifyItems.length > 0) {
          const purifyItem = purifyItems[0];
          const purifyLineIndex = cart.items.indexOf(purifyItem) + 1;
          
          fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              line: purifyLineIndex,
              quantity: 0
            })
          })
          .then(() => fetch('/cart.js'))
          .then(r => r.json())
          .then(updatedCart => {
            document.dispatchEvent(new CustomEvent('cart:refresh', {
              detail: updatedCart
            }));
          });
          return;
        }
        
        if (nordicGlowItems.length === 0 || purifyItems.length === 0) {
          return;
        }
        
        const nordicGlowTotal = nordicGlowItems.reduce((sum, item) => sum + item.quantity, 0);
        const purifyTotal = purifyItems.reduce((sum, item) => sum + item.quantity, 0);
        
        if (purifyTotal !== nordicGlowTotal) {
          const purifyItem = purifyItems[0];
          const purifyLineIndex = cart.items.indexOf(purifyItem) + 1;
          
          const purifyElement = document.getElementById(`CartDrawerItem-${purifyLineIndex}`);
          if (purifyElement) {
            purifyElement.classList.add('updating');
          }
          
          fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              line: purifyLineIndex,
              quantity: nordicGlowTotal
            })
          })
          .then(() => {
            if (purifyElement) {
              purifyElement.classList.remove('updating');
            }
            return fetch('/cart.js');
          })
          .then(r => r.json())
          .then(updatedCart => {
            document.dispatchEvent(new CustomEvent('cart:refresh', {
              detail: updatedCart
            }));
          });
        }
      })
      .catch(error => {
        console.error('Error enforcing gift ratio:', error);
      });
  }
  
  function initBundleRemovalLogic() {
    document.addEventListener('click', function(e) {
      const removeButton = e.target.closest('.product-cart-item .remove');
      if (!removeButton) return;
      
      const hasGift = removeButton.dataset.hasGift === 'true';
      const giftVariantId = removeButton.dataset.giftVariantId;
      
      if (hasGift && giftVariantId) {
        e.preventDefault();
        
        const lineIndex = removeButton.dataset.index;
        
        console.log('🗑️ Removing bundle: main item + gift');
        
        Promise.all([
          fetch(`/cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              line: lineIndex,
              quantity: 0
            })
          }),
          fetch('/cart.js')
            .then(r => r.json())
            .then(cart => {
              const giftItem = cart.items.find(item => 
                item.variant_id === parseInt(giftVariantId)
              );
              if (giftItem) {
                const giftLineIndex = cart.items.indexOf(giftItem) + 1;
                return fetch(`/cart/change.js`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    line: giftLineIndex,
                    quantity: 0
                  })
                });
              }
            })
        ])
        .then(() => {
          return fetch('/cart.js');
        })
        .then(r => r.json())
        .then(cart => {
          console.log('🗑️ Bundle removed, cart item count:', cart.item_count);
          
          if (cart.item_count === 0) {
            console.log('🗑️ Cart is empty, refreshing drawer content');
            
            // Fetch fresh empty cart HTML
            fetch('/?section_id=cart-drawer')
              .then(response => response.text())
              .then(html => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const newContent = doc.querySelector('#Cart-Drawer .side-panel-inner');
                const currentContent = document.querySelector('#Cart-Drawer .side-panel-inner');
                
                if (newContent && currentContent) {
                  console.log('🗑️ Replacing drawer content with empty state');
                  currentContent.innerHTML = newContent.innerHTML;
                  
                  // Clear all counter states and timers
                  CounterManager.states.clear();
                  CounterManager.timers.forEach(({ firstTimer, intervalTimer }) => {
                    clearTimeout(firstTimer);
                    if (intervalTimer) clearInterval(intervalTimer);
                  });
                  CounterManager.timers.clear();
                  console.log('🗑️ Cleared all counter states and timers');
                }
              });
          } else {
            document.dispatchEvent(new CustomEvent('cart:refresh', {
              detail: cart
            }));
          }
        })
        .catch(error => {
          console.error('🗑️ Error removing bundle:', error);
          fetch('/?section_id=cart-drawer')
            .then(response => response.text())
            .then(html => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const newContent = doc.querySelector('#Cart-Drawer .side-panel-inner');
              const currentContent = document.querySelector('#Cart-Drawer .side-panel-inner');
              
              if (newContent && currentContent) {
                currentContent.innerHTML = newContent.innerHTML;
              }
            });
        });
      }
    });
  }
  
  // Initialize Counter Manager
  CounterManager.init();
  
  // Initialize counters immediately if cart drawer already has items
  setTimeout(() => {
    const cartDrawer = document.querySelector('#Cart-Drawer');
    const hasItems = document.querySelectorAll('.e-in-x-cart').length > 0;
    
    if (cartDrawer && hasItems) {
      console.log('🎯 Page load: Cart has items, initializing counters');
      CounterManager.bindAll();
    }
  }, 300); // Increased delay
  
  // Debug function to check timer status
  window.debugCounters = function() {
    console.log('=== COUNTER DEBUG INFO ===');
    console.log('States:', CounterManager.states.size);
    console.log('Timers:', CounterManager.timers.size);
    
    CounterManager.states.forEach((state, variantId) => {
      const hasTimer = CounterManager.timers.has(variantId);
      const hasElement = !!state.element;
      const elementInDOM = state.element && document.contains(state.element);
      
      console.log('Variant ' + variantId + ':', {
        value: state.value,
        direction: state.direction,
        hasTimer,
        hasElement,
        elementInDOM,
        timerInfo: CounterManager.timers.get(variantId)
      });
    });
    console.log('=========================');
  };
  
  // Rebind on cart updates
  document.addEventListener('cart:refresh', function() {
    console.log('🎯 cart:refresh - rebinding');
    setTimeout(() => {
      CounterManager.bindAll();
      hideBundleDiscountCodes();
      enforceGiftRatio();
    }, 200); // Increased delay
  });
  
  document.addEventListener('line-item:change:end', function() {
    console.log('🎯 line-item:change:end - rebinding after delay');
    setTimeout(() => {
      CounterManager.bindAll();
      hideBundleDiscountCodes();
      enforceGiftRatio();
    }, 200); // Increased delay
  });
  
  // Rebind when drawer opens
  const observer = new MutationObserver(function() {
    const cartDrawer = document.querySelector('#Cart-Drawer');
    if (cartDrawer && cartDrawer.classList.contains('active')) {
      console.log('🎯 Drawer opened - rebinding');
      // Longer delay to ensure DOM is ready
      setTimeout(() => {
        CounterManager.bindAll();
        hideBundleDiscountCodes();
      }, 300);
    }
  });
  
  const cartDrawerElement = document.querySelector('#Cart-Drawer');
  if (cartDrawerElement) {
    observer.observe(cartDrawerElement, { 
      attributes: true, 
      attributeFilter: ['class'] 
    });
  }
  
  // Initialize bundle logic
  initBundleRemovalLogic();
  initDiscountWatcher();
  
  console.log('🎯 Option 2 ready');
  
})();